/* global window */
import 'core-js/features/object';
import 'core-js/features/set';
import 'core-js/features/map';
import 'core-js/features/promise/all-settled';

import React from 'react';
import ReactDOM from 'react-dom';
import { loadableReady } from '@loadable/component';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import ConfigProvider from './components/ConfigProvider/ConfigProvider';
import CookieProvider from './components/CookieProvider/CookieProvider';
import LocalStorageProvider from './components/LocalStorageProvider/LocalStorageProvider';
import TrackerProvider from './components/TrackerProvider/TrackerProvider';
import store from './store';
import buildRoutes from './routes/Routes';
import { supportLocalStorage } from 'Helpers/localStorage';
import { getPathParam } from 'Helpers/routeHelper';
import { HelmetProvider } from 'react-helmet-async';
import { initPlush, setTrackerProperties } from 'Helpers/pushNotification';
import uuid from 'uuid/v4';
import Auth from './Auth';
import { setBaseName } from 'Constants/basename';
import { configSelector } from 'Selectors/config';
import { EVENT_HOUSE } from 'Constants/constants';

const VERSION = `v${process.env.PWA_VERSION}`;

const { config, props } = window.__APP;

setTrackerProperties({ props, config });

if (process.env.API_MODE === 'mock') {
    import(/* webpackChunkName: "cypress-bundle" */ '../../cypress/src/browser')
        .then(module => {
            module.worker.start({ quiet: true });
        });
}

if (process.env.NODE_ENV === 'development') {
    import('@welldone-software/why-did-you-render').then(({ 'default': wdyr }) => {
        wdyr(React, { trackAllPureComponents: true });
    });
}

function loadEventHouseConfig() {
    const generalConfig = configSelector(store.getState('config'));
    const eventHouseEnable = generalConfig?.features?.find(feature => feature.name === EVENT_HOUSE)?.enabled;

    if (eventHouseEnable) {
        return fetch('/api/config/event_house').then(res => {
            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }
            return res.json();
        }).then(data => {
            store.dispatch({
                type: 'RECEIVE_EVENT_HOUSE_TRACKING',
                url: '/api/config/event_house',
                data: data?.data
            });
            return data;
        }).catch(error => {
            store.dispatch({
                type: 'RECEIVE_EVENT_HOUSE_TRACKING',
                url: '/api/config/event_house',
                data: config?.eventHouse
            });
            if (window?.newrelic?.noticeError) {
                window?.newrelic?.noticeError(new Error(`[Error] [Event House Config API] ${error}`));
            }
            return config?.eventHouse;
        });
    }
    return '';
}

function prepareRuntimeEnv() {
    try {
        // Remove object from gloabl scope.
        delete window.__APP;

        if (Auth.isLoggedIn()) {
            initPlush();
        }

        // console polyfill
        window.console = window.console || {
            log: () => { },
            error: () => { }
        };

        window.addEventListener('beforeunload', () => {
            if (supportLocalStorage() && window.sessionStorage.getItem('uuid') === window.localStorage.getItem('refreshingToken')) {
                window.localStorage.removeItem('refreshingToken');
            }
        });

        if (supportLocalStorage()) {
            window.sessionStorage.setItem('uuid', uuid());
        }

        return Promise.resolve();
    }
    catch (err) {
        return Promise.reject(err);
    }
}

function loadLocale() {
    let intlLoaded = Promise.resolve();
    let langParam = getPathParam(config.langs) || config.lang;
    const params = langParam.split('-');

    langParam = params[0];
    const countryCode = params[1];
    const fullLangParam = params[1] ? `${langParam }-${ countryCode.toUpperCase()}` : langParam;

    if (!window.Intl) {
        intlLoaded = import(/* webpackChunkName: "intl" */'intl')
            .then(() => import(/* webpackChunkName: "locale-[request]" */`intl/locale-data/jsonp/${langParam}`));
    }

    const callback = data => {
        return data[0];
    };

    const promises = [
        fetch(`/translations?lang=${fullLangParam}&version=${VERSION}`).then(res => res.json()),
        intlLoaded
    ];

    return Promise.all(promises).then(callback).catch(() => callback([{}]));
}

const onError = error => {
    if (window && window.newrelic && window.newrelic.addPageAction) {
        const event = {};
        const errorParsed = error.includes('"') ? error.match(/"([^"]+)"/g).map(x => {
            return x.replace(/"/g, '');
        }) : null;

        if (errorParsed !== null && errorParsed.length === 2) {
            event.missingTranslation = errorParsed[0];
            event.language = errorParsed[1];
            window.newrelic.addPageAction('missingTranslation', event);
        }
    }
};

function renderApp(translations) {
    const routes = buildRoutes(config);
    const langParam = getPathParam(config.langs);
    let getLocale = langParam || config.lang;

    getLocale = getLocale.split('-')[0];

    props.app_language = getLocale;
    const router = createBrowserRouter(routes, {
        basename: langParam ? `/${langParam}` : ''
    });

    setBaseName(router.basename);

    loadableReady(() => {
        ReactDOM.hydrate((
            <HelmetProvider>
                <IntlProvider textComponent="span" locale={ getLocale } messages={ translations } onError={ onError }>
                    <ConfigProvider source={ config }>
                        <CookieProvider>
                            <LocalStorageProvider>
                                <Provider store={ store }>
                                    <TrackerProvider properties={ props }>
                                        <RouterProvider router={ router } />
                                    </TrackerProvider>
                                </Provider>
                            </LocalStorageProvider>
                        </CookieProvider>
                    </ConfigProvider>
                </IntlProvider>
            </HelmetProvider>
        ), document.getElementById('app'));
    });
}
prepareRuntimeEnv()
    .then(loadEventHouseConfig)
    .then(loadLocale)
    .then(renderApp, err => {
        renderApp();
        throw err;
    });

